import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import StaffPasswordResetDialog from "@saleor/staff/components/StaffPasswordResetDialog";
import React from "react";
import { useIntl } from "react-intl";

import { AccountSettingDetailsPage } from "../../components/AccountSettingDetailsPage";
import { AccountSettingDetailsPageFormData } from "../../components/AccountSettingDetailsPage";
import {
  useAccountProfileUpdate,
  useChangeAccountPassword
} from "../../mutations";
import { useAccountSettingDetails } from "../../queries";
import { AccountProfileUpdate } from "../../types/AccountProfileUpdate";
import { ChangeAccountPassword } from "../../types/ChangeAccountPassword";
import {
  accountSettingDetailsUrl,
  AccountSettingDetailsUrlQueryParams
} from "../../urls";
interface AccountSettingDetailsProp {
  id: string;
  params: AccountSettingDetailsUrlQueryParams;
}

export const AccountSettingDetails: React.FC<AccountSettingDetailsProp> = ({
  id,
  params
}) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const user = useUser();
  const intl = useIntl();

  const { data, loading, refetch } = useAccountSettingDetails({
    displayLoader: true,
    variables: {
      id
    }
  });

  const handleUpdateSuccess = (data: AccountProfileUpdate) => {
    if (data.staffUpdate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Profile Updated"
        })
      });
      refetch();
    } else if (data.staffUpdate.errors.some(e => e.code === "REQUIRED")) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Some Fields have null value !"
        })
      });
    } else if (data.staffUpdate.errors.some(e => e.code === "UNIQUE")) {
      notify({
        status: "error",
        text: data.staffUpdate.errors[0].message
      });
    } else {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "There's an error!"
        })
      });
    }
  };
  const [accountUpdate, accountUpdateResult] = useAccountProfileUpdate({
    onCompleted: handleUpdateSuccess
  });

  const handleSubmit = async (data: AccountSettingDetailsPageFormData) => {
    const result = await accountUpdate({
      variables: {
        id,
        input: {
          contactNo: data.contactNo,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName
        }
      }
    });
    return result.data.staffUpdate.errors;
  };

  const closeModal = () =>
    navigate(
      accountSettingDetailsUrl(id, {
        ...params,
        action: undefined
      })
    );

  const handleChangePassword = (data: ChangeAccountPassword) => {
    if (data.passwordChange.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({ defaultMessage: "Changed Password" })
      });
      closeModal();
    }
  };
  const [changePassword, changePasswordOpts] = useChangeAccountPassword({
    onCompleted: handleChangePassword
  });

  const isUserSameAsViewer = user.user?.id === data?.user?.id;

  return (
    <>
      <AccountSettingDetailsPage
        user={data?.user}
        disabled={loading}
        canChangePassword={isUserSameAsViewer}
        onSubmit={handleSubmit}
        onBack={() => navigate("/")}
        onChangePassword={() =>
          navigate(
            accountSettingDetailsUrl(id, {
              action: "change-password"
            })
          )
        }
        saveButtonBarState={accountUpdateResult.status}
      />

      <StaffPasswordResetDialog
        open={params.action === "change-password"}
        confirmButtonState={changePasswordOpts.status}
        errors={changePasswordOpts?.data?.passwordChange?.errors || []}
        onClose={closeModal}
        onSubmit={data =>
          changePassword({
            variables: data
          })
        }
      />
    </>
  );
};

export default AccountSettingDetails;
